import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Loader2, Pencil, Trash2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_COLORS = ["#FFD700", "#FF007F", "#0055FF", "#00C2B8", "#FF6600"];

const EMPTY_TESTIMONIAL = { quote: "", source: "", org: "", color: "#FFD700" };

const inputCls =
  "w-full border-2 border-[#1A1A1A] rounded-xl px-4 py-2.5 text-sm font-semibold focus:outline-none focus:ring-4 focus:ring-[#FFD700] bg-white";

export default function ContentManager({ adminKey, content, onChanged }) {
  const logos = content?.logos || [];
  const testimonials = content?.testimonials || [];

  // ---- Logo upload state ----
  const [logoLabel, setLogoLabel] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);

  // ---- Testimonial form state ----
  const [form, setForm] = useState(EMPTY_TESTIMONIAL);
  const [editingId, setEditingId] = useState(null);
  const [tBusy, setTBUSY] = useState(false);

  const headers = { "X-Admin-Key": adminKey };

  const addLogo = async (e) => {
    e.preventDefault();
    if (!logoLabel.trim()) return toast.error("Add a partner name.");
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("label", logoLabel.trim());
      if (logoFile) fd.append("image", logoFile);
      await axios.post(`${API}/admin/logos`, fd, { headers });
      toast.success(logoFile ? "Logo uploaded." : "Text wordmark added.");
      setLogoLabel("");
      setLogoFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed.");
    } finally {
      setLogoBusy(false);
    }
  };

  const removeLogo = async (id) => {
    try {
      await axios.delete(`${API}/admin/logos/${id}`, { headers });
      toast.success("Logo removed.");
      onChanged();
    } catch {
      toast.error("Could not remove logo.");
    }
  };

  const saveTestimonial = async (e) => {
    e.preventDefault();
    if (!form.quote.trim() || !form.source.trim() || !form.org.trim())
      return toast.error("Quote, name and organisation are all required.");
    setTBUSY(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/testimonials/${editingId}`, form, { headers });
        toast.success("Testimonial updated.");
      } else {
        await axios.post(`${API}/admin/testimonials`, form, { headers });
        toast.success("Testimonial added.");
      }
      setForm(EMPTY_TESTIMONIAL);
      setEditingId(null);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save testimonial.");
    } finally {
      setTBUSY(false);
    }
  };

  const removeTestimonial = async (id) => {
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers });
      toast.success("Testimonial removed.");
      if (editingId === id) {
        setEditingId(null);
        setForm(EMPTY_TESTIMONIAL);
      }
      onChanged();
    } catch {
      toast.error("Could not remove testimonial.");
    }
  };

  return (
    <div className="mt-8 grid lg:grid-cols-2 gap-6" data-testid="content-manager">
      {/* -------- Partner logos -------- */}
      <section className="rounded-3xl bg-white border-2 border-[#1A1A1A] nb-shadow overflow-hidden">
        <div className="p-5 border-b-2 border-[#1A1A1A]">
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#FF007F] font-bold">
            Press strip
          </p>
          <h2 className="mt-1 font-display font-black text-2xl tracking-tighter">
            Partner logos
          </h2>
          <p className="mt-1 text-xs text-[#4A4A4A]">
            Upload a real logo image, or save with just a name for a styled text
            wordmark. Changes go live on the site instantly.
          </p>
        </div>

        <ul className="divide-y divide-[#1A1A1A]/10 max-h-72 overflow-y-auto">
          {logos.map((l) => (
            <li
              key={l.id}
              data-testid={`logo-row-${l.id}`}
              className="flex items-center justify-between gap-3 px-5 py-3"
            >
              <span className="flex items-center gap-3 min-w-0">
                {l.image_path ? (
                  <img
                    src={`${API}/files/${l.image_path}`}
                    alt={l.label}
                    className="h-8 w-auto max-w-[120px] object-contain"
                  />
                ) : (
                  <span className="font-display font-black text-base tracking-tight text-[#1A1A1A]/70">
                    {l.label}
                  </span>
                )}
                {l.image_path && (
                  <span className="text-xs font-semibold text-[#4A4A4A] truncate">
                    {l.label}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => removeLogo(l.id)}
                data-testid={`logo-delete-${l.id}`}
                aria-label={`Remove ${l.label}`}
                className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full border-2 border-[#1A1A1A] bg-white text-[#1A1A1A] hover:bg-[#FF007F] hover:text-white transition-colors"
              >
                <Trash2 size={13} strokeWidth={2.5} />
              </button>
            </li>
          ))}
          {logos.length === 0 && (
            <li className="px-5 py-6 text-sm text-[#4A4A4A]">No logos yet.</li>
          )}
        </ul>

        <form onSubmit={addLogo} className="p-5 border-t-2 border-[#1A1A1A] bg-[#F8F9FA] space-y-3">
          <input
            value={logoLabel}
            onChange={(e) => setLogoLabel(e.target.value)}
            placeholder="Partner name (e.g. City of Sydney)"
            maxLength={80}
            data-testid="logo-label-input"
            className={inputCls}
          />
          <label
            className="flex items-center gap-3 border-2 border-dashed border-[#1A1A1A]/40 rounded-xl px-4 py-3 cursor-pointer hover:border-[#1A1A1A] transition-colors"
            data-testid="logo-file-drop"
          >
            <ImagePlus size={18} strokeWidth={2.5} className="text-[#0055FF] shrink-0" />
            <span className="text-sm font-semibold text-[#4A4A4A] truncate">
              {logoFile ? logoFile.name : "Optional: choose a logo image (PNG, JPG, WebP, SVG)"}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              className="hidden"
              data-testid="logo-file-input"
              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
            />
          </label>
          <button
            type="submit"
            disabled={logoBusy || !logoLabel.trim()}
            data-testid="logo-submit-btn"
            className="inline-flex items-center justify-center gap-2 w-full bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-widest nb-shadow-sm nb-press disabled:opacity-70"
          >
            {logoBusy && <Loader2 size={13} className="animate-spin" />}
            Add to press strip
          </button>
        </form>
      </section>

      {/* -------- Testimonials -------- */}
      <section className="rounded-3xl bg-white border-2 border-[#1A1A1A] nb-shadow overflow-hidden">
        <div className="p-5 border-b-2 border-[#1A1A1A]">
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#0055FF] font-bold">
            Testimonials marquee
          </p>
          <h2 className="mt-1 font-display font-black text-2xl tracking-tighter">
            What partners say
          </h2>
          <p className="mt-1 text-xs text-[#4A4A4A]">
            Add, edit or remove the quotes scrolling across the site.
          </p>
        </div>

        <ul className="divide-y divide-[#1A1A1A]/10 max-h-72 overflow-y-auto">
          {testimonials.map((t) => (
            <li key={t.id} data-testid={`testimonial-row-${t.id}`} className="px-5 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold leading-snug truncate">“{t.quote}”</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
                    <span
                      className="inline-block h-2 w-2 rounded-full border border-[#1A1A1A] mr-1.5 align-middle"
                      style={{ backgroundColor: t.color }}
                    />
                    {t.source} · {t.org}
                  </p>
                </div>
                <span className="flex gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(t.id);
                      setForm({ quote: t.quote, source: t.source, org: t.org, color: t.color });
                    }}
                    data-testid={`testimonial-edit-${t.id}`}
                    aria-label={`Edit testimonial from ${t.source}`}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-full border-2 border-[#1A1A1A] bg-white hover:bg-[#FFD700] transition-colors"
                  >
                    <Pencil size={13} strokeWidth={2.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeTestimonial(t.id)}
                    data-testid={`testimonial-delete-${t.id}`}
                    aria-label={`Remove testimonial from ${t.source}`}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-full border-2 border-[#1A1A1A] bg-white hover:bg-[#FF007F] hover:text-white transition-colors"
                  >
                    <Trash2 size={13} strokeWidth={2.5} />
                  </button>
                </span>
              </div>
            </li>
          ))}
          {testimonials.length === 0 && (
            <li className="px-5 py-6 text-sm text-[#4A4A4A]">No testimonials yet.</li>
          )}
        </ul>

        <form onSubmit={saveTestimonial} className="p-5 border-t-2 border-[#1A1A1A] bg-[#F8F9FA] space-y-3">
          {editingId && (
            <div className="flex items-center justify-between bg-[#FFD700] border-2 border-[#1A1A1A] rounded-xl px-3 py-1.5">
              <p className="text-[11px] font-bold uppercase tracking-widest">Editing testimonial</p>
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(EMPTY_TESTIMONIAL);
                }}
                data-testid="testimonial-cancel-edit"
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest"
              >
                <X size={12} strokeWidth={3} /> Cancel
              </button>
            </div>
          )}
          <textarea
            value={form.quote}
            onChange={(e) => setForm({ ...form, quote: e.target.value })}
            placeholder="The quote, in their words"
            rows={3}
            maxLength={400}
            data-testid="testimonial-quote-input"
            className={`${inputCls} resize-none`}
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              placeholder="Name / role (e.g. Principal)"
              maxLength={120}
              data-testid="testimonial-source-input"
              className={inputCls}
            />
            <input
              value={form.org}
              onChange={(e) => setForm({ ...form, org: e.target.value })}
              placeholder="Organisation"
              maxLength={160}
              data-testid="testimonial-org-input"
              className={inputCls}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
              Accent
            </span>
            {BRAND_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setForm({ ...form, color: c })}
                data-testid={`testimonial-color-${c.replace("#", "")}`}
                aria-label={`Accent colour ${c}`}
                className={`h-7 w-7 rounded-full border-2 border-[#1A1A1A] transition-transform ${
                  form.color === c ? "scale-110 ring-2 ring-offset-2 ring-[#1A1A1A]" : ""
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <button
            type="submit"
            disabled={tBusy}
            data-testid="testimonial-submit-btn"
            className="inline-flex items-center justify-center gap-2 w-full bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-widest nb-shadow-sm nb-press disabled:opacity-70"
          >
            {tBusy && <Loader2 size={13} className="animate-spin" />}
            {editingId ? "Save changes" : "Add testimonial"}
          </button>
        </form>
      </section>
    </div>
  );
}
